import React, { useEffect, useState } from 'react'
import { useStateContext } from '../../context/StateContextAPI'

import faqData from "./faq.questionss.json"

import "./faq.page.css"
import { ContactForm, NewsletterForm } from '../../components'
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styled from '@emotion/styled'
import { MainBackgroundVector } from '../../assets'
import useGoogleTag from '../../utils/gtag'
import { useTranslation } from 'react-i18next'
import faqDataDe from "./faq.questionss.de.json"

// Define types for the FAQ data
interface FAQItem {
  ques: string;
  ans: string;
}

interface FAQData {
  [key: string]: FAQItem[][];
}

const FAQPageContainer = styled.div`
    width: 100%;
`

export const BackgroundImage = styled.img`
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
`

const GeneralLayout = styled.div`
    display: flex;
    gap: 30px;
    padding: 0 var(--global-padding);
    flex-wrap: wrap;
    margin: 50px 0;
`
const FillerBox = styled.div`
    flex: 1;
    min-width: 300px;
    max-width: 450px;
    padding: 50px;
    box-sizing: border-box;
    @media screen and (max-width: 680px) {
        display: none;
    }
`
const HeaderText = styled.div`
    font-size: 4.5em;
    flex: 1;
    margin: 150px 0 50px 0;
    font-family: var(--title-font), sans-serif;
    line-height: 1em;
    letter-spacing: 1px;
    min-width: 300px;
    @media screen and (max-width: 1000px) {
        font-size: 3.5em;
        margin: 80px 0 30px 0;
    }
    @media screen and (max-width: 700px) {
        font-size: 10vw;
    }
`

const FAQNavigatorLinks = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    position: sticky;
    top: 100px;
    gap: 10px;
    min-width: 300px;
    max-width: 450px;
    height: 600px;
    background: #000;
    background: linear-gradient(to bottom, rgba(0,0,0,1), #101010);
    box-sizing: border-box;
    padding: 50px;
    @media screen and (max-width: 680px) {
        display: none;
    }
`

const ContactsLeftContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
    min-width: 300px;
    max-width: 450px;
    background: #000;
    background: linear-gradient(to bottom, rgba(0,0,0,1), #101010);
    box-sizing: border-box;
    padding: 50px;
`

const FAQSection = styled.div`
    flex: 1;
    min-width: 300px;
`

const ContactSection = styled.div`
    flex: 1;
    min-width: 300px;
`

const FaqPage = () => {

    // GTAG
    const gtag = process.env.REACT_APP_GOOGLE_TAG || "";
    useGoogleTag(gtag)

    const { setActivePage } = useStateContext();
    const { t, i18n } = useTranslation();
    const activeFaqData = (i18n.language === 'de' ? faqDataDe : faqData) as FAQData;
    const categories = Object.keys(activeFaqData);
    const [activeCategory, setActiveCategory] = useState<string>(categories[0]);
    const [currentFAQs, setCurrentFAQs] = useState<FAQItem[]>([]);

    useEffect(() => {
        setActivePage(() => "faq")
        window.scrollTo(0, 0);

        // Set initial FAQs to first category
        const firstCategory = Object.keys(activeFaqData)[0];
        setActiveCategory(firstCategory);
        if (activeFaqData[firstCategory] && activeFaqData[firstCategory][0]) {
            setCurrentFAQs(activeFaqData[firstCategory][0]);
        }
    }, [setActivePage, i18n.language])

    const handleCategoryClick = (category: string) => {
        setActiveCategory(category);
        if (activeFaqData[category] && activeFaqData[category][0]) {
            setCurrentFAQs(activeFaqData[category][0]);
        }
    };

    return (
        <>
        <FAQPageContainer>
            <BackgroundImage src={MainBackgroundVector} />
            <GeneralLayout>
                <FillerBox>
                    &nbsp;
                </FillerBox>
                <HeaderText id="rokk-users-faq">
                    <div>
                        <span className="head-txt-primary">{t('faq.title1')}</span>
                    </div>
                    <div>
                        <span className="head-txt-off-white">{t('faq.title2')}</span>
                    </div>
                </HeaderText>
            </GeneralLayout>
            {/* Mobile Category Selector */}
            <div className="faq-mobile-categories">
                {categories.map((category, index) => (
                    <button
                        key={index}
                        className={`faq-mobile-category-btn ${activeCategory === category ? 'active' : ''}`}
                        onClick={() => handleCategoryClick(category)}
                    >
                        {category}
                    </button>
                ))}
            </div>

            <GeneralLayout>
                <FAQNavigatorLinks>
                <Typography
                    sx={{
                        fontFamily: 'var(--head-text)',
                        fontSize: '24px',
                        textTransform: 'uppercase',
                        color: '#ffffff80'
                    }}
                >{t('faq.subtitle')}</Typography>

                {categories.map((category, index) => (
                    <Button
                        key={index}
                        fullWidth
                        onClick={() => handleCategoryClick(category)}
                        sx={{
                            color: activeCategory === category ? '#ff8303' : '#ffffff80',
                            height: '50px',
                            fontFamily: 'Poppins, sans-serif',
                            borderRadius: 0,
                            padding: '10px 20px',
                            borderColor: activeCategory === category ? 'var(--primary)' : '#ffffff80',
                            border: activeCategory === category ? '1px solid #ff8303' : '1px solid #ffffff80',
                            '&:hover': {
                                border: '1px solid #ff8303',
                                background: '#ff830320',
                                color: '#ff8303'
                            }
                        }}
                        variant='outlined'
                    >
                        {category}
                    </Button>
                ))}
                </FAQNavigatorLinks>


            {/* FAQ Hero Section */}
            <FAQSection>
                <div>
                    <div className="faq-section-head-text">{activeCategory.toUpperCase() + t('faq.faqSuffix')}</div>

                    <div className="rokk-app-faq-questions-container">
                        {currentFAQs.map((faq, _idx) => {
                            return (
                                <Accordion
                                    key={_idx}
                                    sx={{
                                        backgroundColor: '#1a1a1a',
                                        background: 'linear-gradient(215.36deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                        borderRadius: '0',
                                        '&.Mui-expanded': {
                                            background: 'rgba(255, 255, 255, 0.15)'
                                        }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                        sx={{
                                            minHeight: '70px',
                                            color: '#ffffff',
                                            '&.Mui-expanded': {
                                                color: '#ff8303',
                                            },
                                        }}
                                    >
                                        <Typography sx={{fontFamily: 'Poppins, sans-serif', fontWeight: 500}}>{faq.ques}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        sx={{
                                            color: '#ffffff'
                                        }}
                                    >
                                        <div dangerouslySetInnerHTML={{ __html: faq.ans }} />
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                    </div>
                </div>
            </FAQSection>
            </GeneralLayout>

            <GeneralLayout style={{margin: '100px 0'}}>
                <ContactsLeftContainer>
                    <Typography sx={{
                        fontSize: '48px',
                        fontFamily: 'var(--head-text), sans-serif',
                        color: 'var(--primary)',
                        lineHeight: '52px'
                    }}>
                        {t('faq.contactTitle')}
                    </Typography>
                    <Typography sx={{fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#ffffff80', textTransform: 'uppercase'}}>
                    {t('faq.contactSubtitle')}
                    </Typography>
                </ContactsLeftContainer>
                <ContactSection>
                    <div className="faq-section-head-text"><span style={{color: '#fff'}}>{t('faq.contactCta1')}</span> {t('faq.contactCta2')} <span style={{color: '#fff'}}>{t('faq.contactCta3')}</span></div>
                    {/* Contact Form: */}
                    <ContactForm />

                </ContactSection>
            </GeneralLayout>


            <div style={{ padding: 'var(--global-padding)' }}>
                <div className="newsletter-container-pricing-page flex aln-items-cnt">
                    <div className="newsletter-section-header flex flex-dir-col">
                        <span className="head-txt-off-white font-sz-small">{t('newsletter.subscribeTo')}</span>
                        <span className="head-txt-off-white font-sz-small">{t('newsletter.our')}
                            <span className="head-txt-primary">{t('newsletter.rokk')}</span>
                        </span>
                        <span className="head-txt-primary font-sz-small">{t('newsletter.newsletter')}</span>
                    </div>
                    <NewsletterForm />
                </div>
            </div>
            </FAQPageContainer>
        </>
    )
}

export default FaqPage
